import { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Brain, X, TrendingDown, Loader2, RefreshCw,
  MessageCircleQuestion, Send, AlertCircle, CornerDownLeft,
} from 'lucide-react';
import { buildFinancialSummary } from '@/lib/financialSummary';

const EXAMPLE_QUESTIONS = [
  'Help me find ways to save $40 per week for the next year',
  'Which subscriptions should I cancel first?',
  'What changed most in my spending this year?',
  'Where am I overspending compared to my budgets?',
];

export default function AIInsightsPanel({ transactions, budgets = [], onClose }) {
  // --- existing auto-generated insights (unchanged behaviour) ---
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  // --- ask-a-question ---
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]); // [{question, answer}]
  const [isAsking, setIsAsking] = useState(false);
  const [askError, setAskError] = useState(null);
  const answersEndRef = useRef(null);

  useEffect(() => {
    generateInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (conversation.length) {
      answersEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [conversation.length]);

  const generateInsights = async () => {
    setIsLoading(true);
    setInsights(null);
    setInsightsError(null);

    const debits = transactions.filter(t => t.type === 'debit');
    const categorySpending = {};
    debits.forEach(t => {
      if (t.category) categorySpending[t.category] = (categorySpending[t.category] || 0) + (t.amount || 0);
    });
    const monthKeys = new Set(debits.map(t => `${t.year}-${t.month}`));
    const monthCount = Math.max(monthKeys.size, 1);
    const totalSpending = Object.values(categorySpending).reduce((a, b) => a + b, 0);
    const spendingLines = Object.entries(categorySpending)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, amt]) => `${cat}: $${amt.toFixed(2)} total ($${(amt / monthCount).toFixed(2)}/month avg)`)
      .join('\n');

    try {
      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `You are a personal finance advisor for an Australian household. Analyse the spending data and provide specific, actionable savings recommendations.
Period: ${monthCount} month(s)
Total Spending: $${totalSpending.toFixed(2)}
Monthly Average: $${(totalSpending / monthCount).toFixed(2)}
Spending by category:
${spendingLines || 'No categorised spending data.'}
Provide 3-5 specific savings recommendations focused on the highest spending categories. Be realistic and Australia-specific (e.g. mention Woolworths, Coles, etc. where relevant). Give concrete dollar amounts.`,
        response_json_schema: {
          type: 'object',
          properties: {
            summary: { type: 'string' },
            total_potential_monthly_savings: { type: 'number' },
            recommendations: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  category: { type: 'string' },
                  current_monthly_avg: { type: 'number' },
                  potential_savings: { type: 'number' },
                  savings_percentage: { type: 'number' },
                  tips: { type: 'array', items: { type: 'string' } },
                },
              },
            },
          },
        },
      });
      setInsights(result);
    } catch (err) {
      // Previously an error here left the spinner running forever, because
      // setIsLoading(false) sat after an unguarded await.
      setInsightsError(err?.message || 'Could not generate insights.');
    } finally {
      setIsLoading(false);
    }
  };

  const askQuestion = async (text) => {
    const q = (text ?? question).trim();
    if (!q || isAsking) return;

    setIsAsking(true);
    setAskError(null);

    const summary = buildFinancialSummary(transactions, budgets);

    // Carry the last couple of exchanges so follow-ups like "what about the
    // groceries one?" make sense, without letting the prompt grow unbounded.
    const history = conversation.slice(-2).map(
      c => `Previous question: ${c.question}\nPrevious answer: ${c.answer.answer}`
    ).join('\n\n');

    try {
      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `You are a personal finance advisor for an Australian household. Answer the user's question using ONLY the financial data below. Be specific and quantitative — cite real categories and dollar figures from the data rather than generic advice.

If the data cannot answer the question, say so plainly instead of guessing.

Use Australian context where relevant (Woolworths, Coles, Aldi, private health, ATO, etc.) and Australian dollars.

=== FINANCIAL DATA ===
${summary.text}
=== END DATA ===
${history ? `\nEarlier in this conversation:\n${history}\n` : ''}
USER QUESTION: ${q}

Answer directly. If the question asks for a savings target, show how the target breaks down against real categories and say whether it is realistic given the actual numbers.`,
        response_json_schema: {
          type: 'object',
          properties: {
            answer: { type: 'string' },
            key_figures: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  label: { type: 'string' },
                  value: { type: 'string' },
                },
              },
            },
            actions: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  title: { type: 'string' },
                  detail: { type: 'string' },
                  monthly_impact: { type: 'number' },
                },
              },
            },
            caveats: { type: 'array', items: { type: 'string' } },
          },
        },
      });

      setConversation(c => [...c, { question: q, answer: result }]);
      setQuestion('');
    } catch (err) {
      setAskError(err?.message || 'Could not answer that question.');
    } finally {
      setIsAsking(false);
    }
  };

  const onKeyDown = (e) => {
    // Enter sends, Shift+Enter for a newline — standard chat behaviour.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  };

  return (
    <Card className="border border-violet-200 shadow-sm bg-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-violet-100 rounded-lg">
              <Brain className="w-4 h-4 text-violet-600" />
            </div>
            <CardTitle className="text-sm font-semibold text-slate-700">AI Spending Insights</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {insights && !isLoading && (
              <Button variant="ghost" size="sm" onClick={generateInsights} className="h-7 text-xs gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </Button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* ---------- Auto-generated insights (existing) ---------- */}
        {isLoading && (
          <div className="flex items-center justify-center py-10 gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
            <span className="text-sm text-slate-500">Analysing your spending patterns...</span>
          </div>
        )}

        {insightsError && !isLoading && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Couldn&apos;t generate insights</p>
              <p className="mt-0.5">{insightsError}</p>
            </div>
          </div>
        )}

        {insights && !isLoading && (
          <div className="space-y-4">
            <div className="p-4 bg-violet-50 rounded-xl border border-violet-100">
              <p className="text-sm text-violet-900">{insights.summary}</p>
              {insights.total_potential_monthly_savings > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-700">
                    Potential savings: ${insights.total_potential_monthly_savings.toFixed(2)}/month
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {insights.recommendations?.map((rec, i) => (
                <div key={i} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                    <span className="font-medium text-sm text-slate-800">{rec.category}</span>
                    <div className="flex gap-1.5">
                      <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200 text-xs">
                        ${rec.current_monthly_avg?.toFixed(0)}/mo
                      </Badge>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                        Save ${rec.potential_savings?.toFixed(0)} ({rec.savings_percentage}%)
                      </Badge>
                    </div>
                  </div>
                  {rec.tips?.length > 0 && (
                    <ul className="space-y-1.5 mt-2">
                      {rec.tips.map((tip, j) => (
                        <li key={j} className="flex gap-2 text-xs text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0 mt-1.5" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---------- Ask a question ---------- */}
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircleQuestion className="w-4 h-4 text-violet-500" />
            <h3 className="text-sm font-semibold text-slate-700">Ask about your finances</h3>
          </div>

          {/* Answers */}
          {conversation.length > 0 && (
            <div className="space-y-4 mb-4">
              {conversation.map((entry, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex gap-2 items-start">
                    <span className="text-xs font-medium text-slate-400 mt-0.5 flex-shrink-0">You</span>
                    <p className="text-sm text-slate-700 font-medium">{entry.question}</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                    <p className="text-sm text-slate-700 whitespace-pre-line">{entry.answer.answer}</p>

                    {entry.answer.key_figures?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {entry.answer.key_figures.map((f, j) => (
                          <Badge key={j} variant="outline" className="bg-white text-slate-600 border-slate-200 text-xs font-normal">
                            <span className="text-slate-400 mr-1">{f.label}</span>
                            <span className="font-semibold">{f.value}</span>
                          </Badge>
                        ))}
                      </div>
                    )}

                    {entry.answer.actions?.length > 0 && (
                      <div className="space-y-2">
                        {entry.answer.actions.map((a, j) => (
                          <div key={j} className="flex gap-2.5 items-start p-2.5 bg-white rounded-lg border border-slate-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0 mt-1.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <p className="text-xs font-semibold text-slate-800">{a.title}</p>
                                {a.monthly_impact > 0 && (
                                  <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap">
                                    ${a.monthly_impact.toFixed(0)}/mo
                                  </span>
                                )}
                              </div>
                              {a.detail && <p className="text-xs text-slate-600 mt-0.5">{a.detail}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {entry.answer.caveats?.length > 0 && (
                      <ul className="space-y-1 pt-1 border-t border-slate-200">
                        {entry.answer.caveats.map((c, j) => (
                          <li key={j} className="text-xs text-slate-400 flex gap-1.5">
                            <span>·</span>{c}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
              <div ref={answersEndRef} />
            </div>
          )}

          {/* Example prompts — only before the first question */}
          {conversation.length === 0 && !isAsking && (
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {EXAMPLE_QUESTIONS.map(ex => (
                <button
                  key={ex}
                  onClick={() => askQuestion(ex)}
                  className="text-xs px-2 py-1 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors text-left"
                >
                  {ex}
                </button>
              ))}
            </div>
          )}

          {askError && (
            <div className="flex items-start gap-2 p-2.5 mb-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{askError}</span>
            </div>
          )}

          {/* Input */}
          <div className="relative">
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={onKeyDown}
              rows={2}
              disabled={isAsking}
              placeholder="e.g. Help me find ways to save $40 per week for the next year"
              className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 pr-11 resize-none outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-100 disabled:bg-slate-50 disabled:text-slate-400"
            />
            <button
              onClick={() => askQuestion()}
              disabled={isAsking || !question.trim()}
              className="absolute right-2 bottom-2.5 p-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:bg-slate-200 disabled:text-slate-400 transition-colors"
              title="Ask"
            >
              {isAsking
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>

          <div className="flex items-center justify-between mt-1.5">
            <p className="text-[10px] text-slate-400 flex items-center gap-1">
              <CornerDownLeft className="w-3 h-3" /> Enter to send · Shift+Enter for a new line
            </p>
            {conversation.length > 0 && (
              <button
                onClick={() => { setConversation([]); setAskError(null); }}
                className="text-[10px] text-slate-400 hover:text-slate-600"
              >
                Clear conversation
              </button>
            )}
          </div>

          <p className="text-[10px] text-slate-400 mt-2">
            Answers are based on the period and categories currently selected on the
            dashboard. Transfers between your own accounts and credit-card repayments
            are excluded.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
